import React from "react";

/**
 * App-level error boundary.
 *
 * Production React shows minified errors that surface as a blank white
 * page when an uncaught render exception happens. This catches that and
 * shows a recoverable UI with a "Reload" button so the user always has
 * a way out — particularly important in the post-payment flow where a
 * crash would otherwise look like the payment failed.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to the browser console — Render's free-tier logs don't capture
    // client-side errors, so console is the only diagnostic channel we
    // have. Wrap in try/catch to be doubly safe.
    try {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, info?.componentStack);
    } catch (_) { /* ignore */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* ignore */ }
  };

  handleHome = () => {
    try {
      window.location.hash = "";
      this.setState({ error: null });
    } catch (_) { /* ignore */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || "An unexpected error occurred";
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#f8fafc",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}>
        <div style={{
          maxWidth: 380,
          width: "100%",
          background: "#fff",
          borderRadius: 16,
          padding: 24,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          textAlign: "center",
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28,
            background: "#fef2f2",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 16px",
          }}>
            <span style={{ fontSize: 28 }}>⚠️</span>
          </div>
          <h2 style={{ margin: 0, fontSize: 18, color: "#0f172a", fontWeight: 700 }}>
            Something went wrong
          </h2>
          <p style={{ margin: "8px 0 16px", fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>
            The page hit an unexpected error. Your data is safe — this is just a UI glitch.
          </p>
          <details style={{ textAlign: "left", marginBottom: 16, fontSize: 11, color: "#94a3b8" }}>
            <summary style={{ cursor: "pointer", userSelect: "none" }}>Technical details</summary>
            <pre style={{
              marginTop: 8, padding: 8,
              background: "#f1f5f9", borderRadius: 6,
              fontSize: 10, overflow: "auto", maxHeight: 120,
              whiteSpace: "pre-wrap",
            }}>{msg}</pre>
          </details>
          <button
            onClick={this.handleReload}
            style={{
              width: "100%",
              padding: "10px 16px",
              borderRadius: 10,
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: 8,
            }}
          >Reload page</button>
          <button
            onClick={this.handleHome}
            style={{
              width: "100%",
              padding: "10px 16px",
              borderRadius: 10,
              background: "transparent",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >Go to home</button>
        </div>
      </div>
    );
  }
}
